import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchNPCServiceQuote, type NPCServiceQuote } from '../service-quote.api';

interface QuoteState {
  ownerKey: string;
  key: string;
  quote: NPCServiceQuote | null;
  lastQuote: NPCServiceQuote | null;
  failed: boolean;
}

const automaticRetryDelays = [1_000, 2_000, 4_000] as const;

export function useNPCServiceQuote(
  heroId: number,
  npcId: number,
  hp: number,
  maxHP: number,
  level: number,
  potions: number,
  enabled: boolean,
) {
  const ownerKey = `${heroId}:${npcId}`;
  const alive = hp > 0;
  const essentialHealing = alive && maxHP > 0 && hp / maxHP < 0.5;
  // Gold and exact HP do not affect prices. Only the inputs used by the server
  // decision belong here, so normal gold/regen updates cannot churn quote GETs.
  const key = `${ownerKey}:${alive}:${essentialHealing}:${level}:${potions}`;
  const [state, setState] = useState<QuoteState>({
    ownerKey: '',
    key: '',
    quote: null,
    lastQuote: null,
    failed: false,
  });
  const [generation, setGeneration] = useState(0);
  const retryState = useRef({ key: '', attempts: 0 });

  useEffect(() => {
    if (!enabled || heroId <= 0 || npcId <= 0) return;
    if (retryState.current.key !== key) retryState.current = { key, attempts: 0 };
    let active = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const request = new AbortController();
    fetchNPCServiceQuote(npcId, request.signal)
      .then((quote) => {
        if (!active || request.signal.aborted) return;
        if (quote.heroId !== heroId || quote.npcId !== npcId) throw new Error('quote owner mismatch');
        setState(() => ({
          ownerKey,
          key,
          quote,
          lastQuote: quote,
          failed: false,
        }));
        retryState.current = { key, attempts: 0 };
      })
      .catch(() => {
        if (!active || request.signal.aborted) return;
        setState((previous) => ({
          ownerKey,
          key,
          quote: null,
          lastQuote: previous.ownerKey === ownerKey ? previous.lastQuote : null,
          failed: true,
        }));
        const attempt = retryState.current.attempts;
        if (attempt < automaticRetryDelays.length) {
          retryState.current.attempts += 1;
          retryTimer = setTimeout(() => setGeneration((value) => value + 1), automaticRetryDelays[attempt]);
        }
      });
    return () => {
      active = false;
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      request.abort();
    };
  }, [enabled, heroId, npcId, ownerKey, key, generation]);

  // Re-read after a service command settles so the displayed price reflects
  // the updated hero state. Transient quote failures retry automatically.
  const reload = useCallback(() => {
    retryState.current = { key, attempts: 0 };
    setState((previous) => ({ ...previous, key: '', quote: null, failed: false }));
    setGeneration((value) => value + 1);
  }, [key]);

  return {
    quote: enabled && state.ownerKey === ownerKey && state.key === key ? state.quote : null,
    lastQuote: enabled && state.ownerKey === ownerKey ? state.lastQuote : null,
    failed: enabled && state.ownerKey === ownerKey && state.key === key && state.failed,
    reload,
  };
}
